/**
 * Per-`Explorer` selection store.
 *
 * WHY THIS EXISTS (perf): `selectedNodeId` / `highlightedNodeId` /
 * `highlightedConnectionIds` used to live on the `TreeContext` value object.
 * Every `TreeNode` row consumes that context, so a single selection change
 * (which right-clicking a row also triggers — see `TreeNodeContent`'s
 * `onContextMenu`) invalidated the context and re-rendered EVERY row in the
 * tree. On a real Object Explorer with ~1,000 expanded rows that was ~60ms of
 * scripting standing between the right-click and the context menu appearing.
 *
 * Holding the same three fields in a zustand store instead lets each row
 * subscribe to a plain boolean scoped to its own id (`selectedNodeId === myId`),
 * so a selection change re-renders exactly two rows — the one losing selection
 * and the one gaining it — and `React.memo` on `TreeNode` keeps every other
 * subtree out of the render entirely.
 *
 * The store is created PER `Explorer` INSTANCE (not a module singleton) and
 * handed down through `TreeContext`, so two Explorers on screen never share
 * a selection.
 */
import { createStore, useStore } from 'zustand';

export interface TreeSelectionState {
  selectedNodeId?: string;
  highlightedNodeId?: string;
  highlightedConnectionIds?: Set<string>;
}

export type TreeSelectionStore = ReturnType<typeof createTreeSelectionStore>;

export function createTreeSelectionStore(initial: TreeSelectionState = {}) {
  return createStore<TreeSelectionState>()(() => initial);
}

/**
 * Read a derived slice of the selection state. Selectors MUST return a
 * primitive (a boolean / string / number) — returning a fresh object or array
 * would defeat zustand's identity check and re-render the row on every store
 * write, which is exactly the cost this store exists to remove.
 */
export function useTreeSelection<T>(
  store: TreeSelectionStore,
  selector: (state: TreeSelectionState) => T,
): T {
  return useStore(store, selector);
}
